import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ImageUp, Paperclip, Trash2, Upload } from 'lucide-react';
import { Botao, Cartao, Selo } from '../../componentes/base';
import { Alternador, Entrada, Rotulo } from '../../componentes/formulario';
import { nomeEhDoProduto } from '@malgoos/shared';
import { AssinaturaMalgo, SimboloMalgo } from '../../componentes/assinatura';
import { Confirmar } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { api, del, get, mensagemDeErro } from '../../nucleo/api';
import { FONTES_PREVIAS, registrarFonte, resolverFonte, urlArquivoAparencia, pilhaFonte } from '../../nucleo/fontes';
import { nomeDeArquivo } from '../../nucleo/formato';
import { SeletorEstilo } from '../../componentes/seletor-estilo';
import { ehEstilo, ESTILO_PADRAO, type Estilo } from '../../nucleo/estilos';
import { podeNoWorkspace, useSessao } from '../../nucleo/sessao';
import type { ArquivoAparencia } from './tipos';

/**
 * Fase 2 (W4, A2 e A4): Configurações > Aparência ganha a fonte do texto do workspace (prévias livres e fonte enviada pelo admin,
 * com a declaração de licença) e a marca do cliente (nome exibido e logo PNG ou WebP). O arquivo é validado pelos bytes no
 * servidor; aqui só a escolha, o envio e a prévia.
 */

const CHAVE_ARQUIVOS = ['configuracoes', 'aparencia', 'arquivos'];
const LIMITES_KB = { woff2: 400, woff: 600, ttf: 1024, otf: 1024 };

function useArquivos() {
  return useQuery({ queryKey: CHAVE_ARQUIVOS, queryFn: () => get<{ itens: ArquivoAparencia[] }>('/configuracoes/aparencia/arquivos') });
}

function enviarArquivo(tipo: 'fonte' | 'logo', arquivo: File, extra: Record<string, string> = {}) {
  const f = new FormData();
  // Campos antes do arquivo: o servidor lê os campos que chegam antes dele no multipart.
  f.append('tipo', tipo);
  for (const [k, v] of Object.entries(extra)) f.append(k, v);
  f.append('arquivo', arquivo);
  return api<{ id: string; sha256: string }>('POST', '/configuracoes/aparencia/arquivos', f);
}

function OpcaoFonte({ marcado, desabilitado, aoEscolher, estilo, children }: { marcado: boolean; desabilitado?: boolean; aoEscolher: () => void; estilo?: CSSProperties; children: ReactNode }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={marcado}
      disabled={desabilitado}
      onClick={aoEscolher}
      style={estilo}
      className={`flex min-h-10 w-full min-w-0 flex-col rounded-lg border-2 px-3 py-2 text-left text-sm disabled:opacity-60 ${marcado ? 'border-acento-forte bg-acento-suave text-tinta-1' : 'border-tinta-3 text-tinta-2 hover:bg-sup-2'}`}
    >
      {children}
    </button>
  );
}

export function CartaoFonteWorkspace() {
  const { t } = useTranslation('configuracoes');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const podeEditar = podeNoWorkspace(s, 'configuracoes.editar');
  const arquivos = useArquivos();
  const enviadas = useMemo(() => (arquivos.data?.itens ?? []).filter((a) => a.tipo === 'fonte'), [arquivos.data]);
  const salva = s.aparencia?.fonte ?? 'hanken';
  const permitidaSalva = s.aparencia?.permitir_fonte_pessoal !== false;
  const [fonte, setFonte] = useState(salva);
  const [permitir, setPermitir] = useState(permitidaSalva);
  const sujo = fonte !== salva || permitir !== permitidaSalva;
  const opcoes = useMemo(() => [...FONTES_PREVIAS.map((f) => f.chave as string), ...enviadas.map((f) => `arquivo:${f.sha256}`)], [enviadas]);
  const chaves = opcoes.join('|');
  useEffect(() => {
    for (const o of chaves.split('|')) void registrarFonte(resolverFonte(o));
  }, [chaves]);
  const nomeDe = (chave: string) => {
    if (chave.startsWith('arquivo:')) return enviadas.find((f) => `arquivo:${f.sha256}` === chave)?.nome ?? chave;
    const p = FONTES_PREVIAS.find((f) => f.chave === chave);
    return p?.chave === 'sistema' ? t('aparencia.fonte.sistema') : (p?.nome ?? chave);
  };

  const salvar = useMutation({
    mutationFn: () => api('PUT', '/configuracoes/aparencia', { fonte, permitir_fonte_pessoal: permitir }),
    onSuccess: async () => {
      await s.recarregar();
      await qc.invalidateQueries({ queryKey: ['configuracoes', 'aparencia', 'historico'] });
      toast.sucesso(tn('comum.salvo'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });

  // Envio de fonte: arquivo, nome exibido e a caixa de licença (obrigatória).
  const entradaArquivo = useRef<HTMLInputElement>(null);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [nome, setNome] = useState('');
  const [licenca, setLicenca] = useState(false);
  const [erroEnvio, setErroEnvio] = useState<string | null>(null);
  const enviar = useMutation({
    mutationFn: () => enviarArquivo('fonte', arquivo!, { nome, licenca: String(licenca) }),
    onSuccess: async () => {
      setArquivo(null);
      setNome('');
      setLicenca(false);
      if (entradaArquivo.current) entradaArquivo.current.value = '';
      await qc.invalidateQueries({ queryKey: CHAVE_ARQUIVOS });
      toast.sucesso(t('aparencia.fonte.enviada'));
    },
    onError: (e) => setErroEnvio(mensagemDeErro(e)),
  });
  const [remover, setRemover] = useState<ArquivoAparencia | null>(null);
  const removerFonte = useMutation({
    mutationFn: (a: ArquivoAparencia) => del(`/configuracoes/aparencia/arquivos/${a.id}`),
    onSuccess: async () => {
      setRemover(null);
      await qc.invalidateQueries({ queryKey: CHAVE_ARQUIVOS });
      toast.sucesso(tn('comum.removido'));
    },
    onError: (e) => {
      setRemover(null);
      toast.erro(mensagemDeErro(e));
    },
  });

  return (
    <Cartao titulo={t('aparencia.fonte.titulo')} className="xl:col-span-2">
      <div className="flex flex-col gap-5" data-fonte-workspace>
        <p className="text-sm text-tinta-3">{t('aparencia.fonte.ajuda')}</p>
        <div role="radiogroup" aria-label={t('aparencia.fonte.titulo')} className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
          {opcoes.map((o) => (
            <OpcaoFonte key={o} marcado={fonte === o} desabilitado={!podeEditar} aoEscolher={() => setFonte(o)} estilo={{ fontFamily: pilhaFonte(resolverFonte(o).familia) }}>
              <span className="flex flex-wrap items-center gap-2 font-medium">
                {nomeDe(o)}
                {o === 'hanken' && <Selo>{t('aparencia.fonte.padrao')}</Selo>}
                {o.startsWith('arquivo:') && <Selo cor="roxo">{t('aparencia.fonte.daEmpresa')}</Selo>}
              </span>
              <span className="text-xs text-tinta-3">{t('aparencia.fonte.amostra')}</span>
            </OpcaoFonte>
          ))}
        </div>
        <div className="flex items-center justify-between gap-3 rounded-lg border border-borda px-3 py-2">
          <div className="min-w-0">
            <p className="text-sm font-medium text-tinta-1">{t('aparencia.fonte.permitir')}</p>
            <p className="text-xs text-tinta-3">{t('aparencia.fonte.permitirAjuda')}</p>
          </div>
          <Alternador marcado={permitir} aoMudar={setPermitir} rotulo={t('aparencia.fonte.permitir')} desabilitado={!podeEditar} />
        </div>
        {podeEditar && (
          <div className="flex flex-wrap justify-end gap-2">
            <Botao
              variante="fantasma"
              disabled={!sujo}
              onClick={() => {
                setFonte(salva);
                setPermitir(permitidaSalva);
              }}
            >
              {t('aparencia.descartar')}
            </Botao>
            {/* Review r16 visual M2: o primário da aba é o "Salvar aparência" do cabeçalho; os salvar dos cartões são secundários. */}
            <Botao disabled={!sujo} carregando={salvar.isPending} onClick={() => salvar.mutate()}>
              {t('aparencia.fonte.salvar')}
            </Botao>
          </div>
        )}

        {podeEditar && (
          <section aria-label={t('aparencia.fonte.enviarTitulo')} className="flex flex-col gap-3 border-t border-borda pt-4" data-enviar-fonte>
            <h3 className="text-sm font-semibold text-tinta-1">{t('aparencia.fonte.enviarTitulo')}</h3>
            <p className="text-xs text-tinta-3">{t('aparencia.fonte.enviarAjuda', LIMITES_KB)}</p>
            {enviadas.length > 0 && (
              <ul className="flex flex-col divide-y divide-borda rounded-lg border border-borda">
                {enviadas.map((a) => (
                  <li key={a.id} className="flex items-center justify-between gap-3 px-3 py-2">
                    <span className="min-w-0">
                      <span className="block truncate text-sm text-tinta-1" title={a.nome} style={{ fontFamily: pilhaFonte(resolverFonte(`arquivo:${a.sha256}`).familia) }}>
                        {a.nome}
                      </span>
                      <span className="numero text-xs text-tinta-3">
                        {a.formato} · {Math.ceil(a.tamanho / 1024)} KB
                      </span>
                    </span>
                    <Botao tamanho="sm" variante="perigo" icone={<Trash2 className="size-4" aria-hidden />} onClick={() => setRemover(a)}>
                      {tn('comum.excluir')}
                    </Botao>
                  </li>
                ))}
              </ul>
            )}
            <div className="grid gap-3 sm:grid-cols-2">
              <Rotulo id="fonte-arquivo" rotulo={t('aparencia.fonte.arquivo')} erro={erroEnvio ?? undefined}>
                {/* Review r16 visual B3: o `Botao` com a entrada de arquivo oculta, como na importação (o campo nativo mostrava
                    "Choose File / No file chosen", fora do idioma do app). */}
                <div className="flex min-w-0 flex-wrap items-center gap-3">
                  <Botao icone={<Paperclip className="size-4" aria-hidden />} aria-describedby={erroEnvio ? 'fonte-arquivo-erro' : undefined} onClick={() => entradaArquivo.current?.click()}>
                    {arquivo ? tn('importacao.trocarArquivo') : tn('importacao.escolherArquivo')}
                  </Botao>
                  {arquivo && (
                    <span className="min-w-0 truncate text-sm text-tinta-2" title={nomeDeArquivo(arquivo.name)}>
                      {nomeDeArquivo(arquivo.name)}
                    </span>
                  )}
                  <input
                    ref={entradaArquivo}
                    id="fonte-arquivo"
                    type="file"
                    accept=".woff2,.woff,.ttf,.otf,font/woff2,font/woff,font/ttf,font/otf"
                    className="sr-only"
                    tabIndex={-1}
                    title={t('aparencia.fonte.arquivo')}
                    aria-invalid={!!erroEnvio || undefined}
                    aria-describedby={erroEnvio ? 'fonte-arquivo-erro' : undefined}
                    onChange={(e) => {
                      setErroEnvio(null);
                      setArquivo(e.target.files?.[0] ?? null);
                    }}
                  />
                </div>
              </Rotulo>
              <Rotulo id="fonte-nome" rotulo={t('aparencia.fonte.nome')}>
                <Entrada id="fonte-nome" maxLength={60} value={nome} onChange={(e) => setNome(e.target.value)} />
              </Rotulo>
            </div>
            <label className="flex cursor-pointer items-start gap-2 text-sm text-tinta-2">
              <input type="checkbox" className="mt-0.5" checked={licenca} onChange={(e) => setLicenca(e.target.checked)} data-licenca-fonte />
              <span>{t('aparencia.fonte.licenca')}</span>
            </label>
            <p className="text-xs text-tinta-3">{t('aparencia.fonte.licencaAviso')}</p>
            <div className="flex justify-end">
              <Botao icone={<Upload className="size-4" aria-hidden />} disabled={!arquivo || !licenca || enviadas.length >= 2} carregando={enviar.isPending} onClick={() => enviar.mutate()}>
                {t('aparencia.fonte.enviar')}
              </Botao>
            </div>
          </section>
        )}
        <Confirmar
          aberto={!!remover}
          aoFechar={() => setRemover(null)}
          aoConfirmar={() => remover && removerFonte.mutate(remover)}
          carregando={removerFonte.isPending}
          perigoso
          titulo={t('aparencia.fonte.removerTitulo')}
          texto={t('aparencia.fonte.removerTexto', { nome: remover?.nome ?? '' })}
        />
      </div>
    </Cartao>
  );
}

export function CartaoMarca() {
  const { t } = useTranslation('configuracoes');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const podeEditar = podeNoWorkspace(s, 'configuracoes.editar');
  const arquivos = useArquivos();
  const nomeSalvo = s.aparencia?.marca_nome ?? '';
  const logo = s.aparencia?.marca_logo ?? null;
  const [nome, setNome] = useState(nomeSalvo);
  const [erro, setErro] = useState<string | null>(null);
  const entradaLogo = useRef<HTMLInputElement>(null);
  const depois = async () => {
    await s.recarregar();
    await qc.invalidateQueries({ queryKey: CHAVE_ARQUIVOS });
    await qc.invalidateQueries({ queryKey: ['configuracoes', 'aparencia', 'historico'] });
  };
  const salvarNome = useMutation({
    mutationFn: () => api('PUT', '/configuracoes/aparencia', { marca_nome: nome.trim() || null }),
    onSuccess: async () => {
      await depois();
      toast.sucesso(tn('comum.salvo'));
    },
    onError: (e) => setErro(mensagemDeErro(e)),
  });
  // A logo enviada já vira a do workspace; a anterior sai da lista (fica no disco para a auditoria).
  const enviarLogo = useMutation({
    mutationFn: async (f: File) => {
      const anterior = arquivos.data?.itens.find((a) => a.tipo === 'logo' && a.sha256 === logo);
      const r = await enviarArquivo('logo', f, { nome: f.name.slice(0, 60) });
      await api('PUT', '/configuracoes/aparencia', { marca_logo: r.sha256 });
      if (anterior) await del(`/configuracoes/aparencia/arquivos/${anterior.id}`).catch(() => undefined);
    },
    onSuccess: async () => {
      if (entradaLogo.current) entradaLogo.current.value = '';
      await depois();
      toast.sucesso(t('aparencia.marca.logoEnviada'));
    },
    onError: (e) => {
      if (entradaLogo.current) entradaLogo.current.value = '';
      toast.erro(mensagemDeErro(e));
    },
  });
  const removerLogo = useMutation({
    mutationFn: async () => {
      const atual = arquivos.data?.itens.find((a) => a.tipo === 'logo' && a.sha256 === logo);
      await api('PUT', '/configuracoes/aparencia', { marca_logo: null });
      if (atual) await del(`/configuracoes/aparencia/arquivos/${atual.id}`).catch(() => undefined);
    },
    onSuccess: async () => {
      await depois();
      toast.sucesso(tn('comum.removido'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  return (
    <Cartao titulo={t('aparencia.marca.titulo')}>
      <div className="flex flex-col gap-4" data-marca-workspace>
        <p className="text-sm text-tinta-3">{t('aparencia.marca.ajuda')}</p>
        <div className="flex min-w-0 items-center gap-3 rounded-lg border border-borda bg-sup-2 p-3" aria-label={t('aparencia.previa')}>
          {!logo && nomeEhDoProduto(nome) ? (
            <>
              <span className="text-tinta-1">
                <AssinaturaMalgo />
              </span>
              <span className="sr-only">MalgoOS</span>
            </>
          ) : (
            <>
              {logo ? <img src={urlArquivoAparencia(logo)} alt={t('aparencia.marca.logo')} className="size-10 shrink-0 rounded-lg object-contain" /> : <SimboloMalgo className="size-10 text-tinta-1" />}
              <span className="min-w-0 truncate text-lg font-semibold tracking-tight" title={nome.trim() || 'MalgoOS'}>
                {nome.trim() || 'MalgoOS'}
              </span>
            </>
          )}
        </div>
        <Rotulo id="marca-nome" rotulo={t('aparencia.marca.nome')} ajuda={t('aparencia.marca.nomeAjuda')} erro={erro ?? undefined}>
          <Entrada id="marca-nome" maxLength={60} disabled={!podeEditar} value={nome} aria-invalid={!!erro || undefined} aria-describedby={erro ? 'marca-nome-erro' : 'marca-nome-ajuda'} onChange={(e) => setNome(e.target.value)} />
        </Rotulo>
        {podeEditar && (
          <>
            <div className="flex justify-end">
              <Botao disabled={nome.trim() === nomeSalvo.trim()} carregando={salvarNome.isPending} onClick={() => salvarNome.mutate()}>
                {t('aparencia.marca.salvarNome')}
              </Botao>
            </div>
            <div className="flex flex-col gap-2 border-t border-borda pt-4">
              <Rotulo id="marca-logo" rotulo={t('aparencia.marca.logo')} ajuda={t('aparencia.marca.logoAjuda')}>
                {/* Review r16 visual B3: o `Botao` com a entrada oculta, como na importação; escolher já envia a logo. */}
                <div className="flex min-w-0 flex-wrap items-center gap-3">
                  <Botao icone={<Paperclip className="size-4" aria-hidden />} aria-describedby="marca-logo-ajuda" ocupado={enviarLogo.isPending} onClick={() => entradaLogo.current?.click()}>
                    {logo ? tn('importacao.trocarArquivo') : tn('importacao.escolherArquivo')}
                  </Botao>
                  <input
                    ref={entradaLogo}
                    id="marca-logo"
                    type="file"
                    accept="image/png,image/webp,.png,.webp"
                    className="sr-only"
                    tabIndex={-1}
                    title={t('aparencia.marca.logo')}
                    aria-describedby="marca-logo-ajuda"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) enviarLogo.mutate(f);
                    }}
                  />
                </div>
              </Rotulo>
              <div className="flex flex-wrap justify-end gap-2">
                {enviarLogo.isPending && (
                  <Botao carregando icone={<ImageUp className="size-4" aria-hidden />}>
                    {t('aparencia.marca.enviando')}
                  </Botao>
                )}
                {logo && (
                  <Botao variante="perigo" icone={<Trash2 className="size-4" aria-hidden />} carregando={removerLogo.isPending} onClick={() => removerLogo.mutate()}>
                    {t('aparencia.marca.removerLogo')}
                  </Botao>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </Cartao>
  );
}

/**
 * N5 (28/10): estilo de interface do workspace (as mesmas amostras de Minha conta) e se cada pessoa pode escolher o próprio. O
 * admin ainda fixa o estilo de pessoas específicas em Pessoas > Usuários e acessos > Membros.
 */
export function CartaoEstiloWorkspace() {
  const { t } = useTranslation('configuracoes');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const podeEditar = podeNoWorkspace(s, 'configuracoes.editar');
  const salvo: Estilo = ehEstilo(s.aparencia?.estilo_padrao) ? s.aparencia.estilo_padrao : ESTILO_PADRAO;
  const permitidoSalvo = s.aparencia?.permitir_estilo_pessoal !== false;
  const [estilo, setEstilo] = useState<Estilo>(salvo);
  const [permitir, setPermitir] = useState(permitidoSalvo);
  const sujo = estilo !== salvo || permitir !== permitidoSalvo;
  // rcor-1 ROB-M1: modelo aplicado no cartão ao lado muda o salvo. Sem edição em aberto, o cartão acompanha; ao salvar, só vai o que mudou.
  const [salvoAntes, setSalvoAntes] = useState({ salvo, permitidoSalvo });
  if (salvoAntes.salvo !== salvo || salvoAntes.permitidoSalvo !== permitidoSalvo) {
    setSalvoAntes({ salvo, permitidoSalvo });
    if (estilo === salvoAntes.salvo) setEstilo(salvo);
    if (permitir === salvoAntes.permitidoSalvo) setPermitir(permitidoSalvo);
  }
  const salvar = useMutation({
    mutationFn: () => api('PUT', '/configuracoes/aparencia', { ...(estilo !== salvo ? { estilo_padrao: estilo } : {}), ...(permitir !== permitidoSalvo ? { permitir_estilo_pessoal: permitir } : {}) }),
    onSuccess: async () => {
      await s.recarregar();
      await qc.invalidateQueries({ queryKey: ['configuracoes', 'aparencia', 'historico'] });
      toast.sucesso(tn('comum.salvo'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  return (
    <Cartao titulo={t('aparencia.estilo.titulo')} className="xl:col-span-2">
      <div className="flex flex-col gap-5" data-estilo-workspace>
        <p className="text-sm text-tinta-3">{t('aparencia.estilo.ajuda')}</p>
        <SeletorEstilo valor={estilo} aoEscolher={(e) => e && setEstilo(e)} desabilitado={!podeEditar} rotulo={t('aparencia.estilo.titulo')} />
        <div className="flex items-center justify-between gap-3 rounded-lg border border-borda px-3 py-2">
          <div className="min-w-0">
            <p className="text-sm font-medium text-tinta-1">{t('aparencia.estilo.permitir')}</p>
            <p className="text-xs text-tinta-3">{t('aparencia.estilo.permitirAjuda')}</p>
          </div>
          <Alternador marcado={permitir} aoMudar={setPermitir} rotulo={t('aparencia.estilo.permitir')} desabilitado={!podeEditar} />
        </div>
        {podeEditar && (
          <div className="flex flex-wrap justify-end gap-2">
            <Botao
              variante="fantasma"
              disabled={!sujo}
              onClick={() => {
                setEstilo(salvo);
                setPermitir(permitidoSalvo);
              }}
            >
              {t('aparencia.descartar')}
            </Botao>
            <Botao disabled={!sujo} carregando={salvar.isPending} onClick={() => salvar.mutate()}>
              {t('aparencia.estilo.salvar')}
            </Botao>
          </div>
        )}
      </div>
    </Cartao>
  );
}
